import { useEffect, useState, Utils, useCallback, useMemo } from "react";
import * as global from '../data/global';
import { MuiMaterials } from '../@index.js';
import { createApiEndpoint } from '../index.js';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js'
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
)

const SensorTemperatureGraph = ({ filterOptions }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [seedData, setSeedData] = useState([{
        labels: [],
        datasets: [],
    }]);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    const options = useMemo(() => {
        const isLight = global.gw.theme === 'light';
        return {
    maintainAspectRatio: false,
    responsive: true,
    layout: {
        padding: {
            top: 0,
            right: 40,
            bottom: 0, 
            left: 40, 
        },
        minWidth: 0,
        //width: 1360, // Width in pixels
        //height: 243, // Height in pixels
    },
    interaction: {
        mode: 'index',
        intersect: false,
    },
    scales: {
        x: {
            dragData: false,
            grid: {
                color: isLight ? "rgba(51, 51, 51, 0.33)" : "rgb(255, 193, 78, 0.33)" // Set the grid color
            },
            ticks: {
                color: isLight ? "#333333" : "white",
                font: {
                    size: 8,
                }
            },
            //title: {
            //    display: true,
            //    text: 'X-axis Label',
            //    color: 'red' // Change the color here
            //}
        },
        y: {
            dragData: false,
            beginAtZero: true,
            min: 20,
            max: 160,
            stepSize: 40,
            //suggestedMin: 0,
            //suggestedMax: 10,
            grid: {
                color: isLight ? "rgba(51, 51, 51, 0.33)" : "rgb(255, 193, 78, 0.33)" // Set the grid color
            },
            ticks: {
                color: isLight ? "#333333" : "white",
                font: {
                    size: 8,
                }
                //callback: function (label, index, labels) {
                //    return label;
                //},
            },
        }
    },
    plugins: {
        legend: {
            display: false
        },
        tooltips: {
            enabled: false
        },
        title: {
            display: true,
            color: isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)",
            align: "center",
            fontSize: 40,
            position: "bottom",
            padding: {
                top: 10,
            }
        },
        //filler: {
        //    propagate: false, // Prevent filling between datasets
        //},
        //legend: {
        //    labels: {
        //        color: "rgb(244, 204, 142)",
        //    }
        //}
        //annotation: {
        //    clip: false, // <-- Adding this property you can draw outside of the chart area
        //    //annotations: {
        //    //    // Create them static
        //    //    label1: {
        //    //        type: 'label',
        //    //        xValue: 2,
        //    //        yValue: 100,
        //    //        backgroundColor: '#ffffff',
        //    //        color: 'red',
        //    //        content: ['static label'],

        //    //    }
        //    //}
        //},
    },
    };
    }, [forceUpdate]);

    let binId = filterOptions.binId;
    let binName = filterOptions.binName;
    let cableId = filterOptions.cableId;
    let cableName = filterOptions.cableName;
    let sensorId = filterOptions.sensorId;
    let sensorName = filterOptions.sensorName;
    let dateFrom = filterOptions.dateFrom;
    let dateTo = filterOptions.dateTo;
    let hourFrom = filterOptions.hourFrom;
    let hourTo = filterOptions.hourTo;

    // Create options with dynamic title
    const chartOptions = useMemo(() => {
        const opts = { ...options };
        if (opts.plugins && opts.plugins.title) {
            opts.plugins.title.text = `     ${binName || ''}   ${cableName || ''}   ${sensorName || ''}`;
        }
        return opts;
    }, [options, binName, cableName, sensorName]);

    useEffect(() => {
        if (filterOptions.binId == null || !filterOptions.dateFrom || !filterOptions.dateTo) {
            return;
        }

        let cancelled = false;

        (async () => {
            await fetchHistoryDates(filterOptions, () => cancelled);
        })();

        return () => {
            cancelled = true;
        };
    }, [filterOptions]);

    const fetchHistoryDates = async (filters, isCancelled) => {
        setIsLoading(true);

        let chartFilters = window.document.getElementsByClassName("chartFilter");
        if (chartFilters && chartFilters.length > 0) {
            let chartFilter = chartFilters[0];
            if (chartFilter) {
                chartFilter.style.display = "none";
                chartFilter.childNodes[0].style.color = getThemeColor("rgba(51, 51, 51, 0.38)", "#f4cc8e61");
            }
        }

        let requestBinId = filters.binId;
        let requestCableId = filters.cableId;
        let requestSensorId = filters.sensorId;
        const requestDateFrom = filters.dateFrom;
        const requestDateTo = filters.dateTo;
        const requestHourFrom = filters.hourFrom;
        const requestHourTo = filters.hourTo;

        const ctask = () => {
            if (!requestBinId || requestBinId == 0) {
                requestBinId = null;
            }
            if (!requestCableId || requestCableId === 0) {
                requestCableId = null;
            }
            if (!requestSensorId || requestSensorId === 0) {
                requestSensorId = null;
            }
            const parsedSensorId = requestSensorId == null ? null : parseInt(requestSensorId, 10);
            return createApiEndpoint().fetchSearchTemperatureHistory(
                "compressed",
                requestBinId,
                null,
                requestCableId,
                null,
                parsedSensorId,
                null,
                new Date(requestDateFrom),
                new Date(requestDateTo),
                requestHourFrom,
                requestHourTo,
                true
            );
        };
        ctask().then((response) => {
            if (isCancelled && isCancelled()) {
                return;
            }

            const historyRows = response?.data?.fetchSearchTemperatureHistory;
            if (Array.isArray(historyRows)) {
                let labels = [];
                let datasets = [];

                if (historyRows.length > 0) {
                    const daysGroup = Object.groupBy(historyRows, ({ dateCableRead }) => new Date(dateCableRead).toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", ""));
                    labels = Object.keys(daysGroup);

                    const sensorNameGroup = Object.groupBy(historyRows, ({ sensorName }) => sensorName);
                    const sensorNameList = Object.keys(sensorNameGroup);
                    const sensorIdGroup = Object.groupBy(historyRows, ({ sensorId }) => sensorId);
                    const sensorIdList = Object.keys(sensorIdGroup);

                    for (let l = 0; l < sensorNameList.length; l++) {
                        let sensorName = sensorNameList[l];
                        let dataSet = {
                            label: sensorName,
                            data: [],
                            tension: 0.4,
                            fill: false,
                            sensorId: sensorIdList[l],
                            spanGaps: true,
                            pointRadius: 1,
                        };
                        datasets.push(dataSet);

                        let sg = sensorNameGroup[sensorName];
                        let temperatureList = sg.map((x) => x.temperature);

                        dataSet.borderColor = global.getHighestTemperatureColor(temperatureList);
                        for (let i = 0; i < labels.length; i++) {
                            let day = labels[i];
                            let ths = sg.filter(x => new Date(x.dateCableRead).toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", "") === day);

                            if (ths && ths.length > 0 && ths[0].temperature > -40) {
                                dataSet.data.push(ths[0].temperature);
                            }
                            else {
                                dataSet.data.push(null);
                            }
                        }
                    }
                }

                setSeedData([{
                    labels: labels,
                    datasets: datasets,
                }]);
                setIsLoading(false);

                window.scrollTo(0, 0);
            }
            else {
                setIsLoading(false);
            }
        }).catch(function (error) {
            if (!isCancelled || !isCancelled()) {
                setIsLoading(false);
            }
        });
    }

    return (
        <MuiMaterials.Box
            sx={{
                height: "100%",
            }}
        >
            <MuiMaterials.Box
                key={`history-title-${global.gw.theme}-${forceUpdate}`}
                sx={{
                    padding: "10px 7px",
                    //marginBottom: "15px",
                    color: getThemeColor('#333333', '#f4cc8e'),
                }}
            >
                Sensor History Graph
            </MuiMaterials.Box>
            <MuiMaterials.Box
                className="sensorChart"
                sx={{
                    position: "relative",
                    flex: 1,
                    minHeight: 280,
                    height: "76% !important",
                }}
            >
                <Line
                    key={`history-chart-${global.gw.theme}-${forceUpdate}`}
                    className={"sensorLine"}
                    data={seedData[0]}
                    options={chartOptions}
                />
                {isLoading ? <MuiMaterials.Box
                    key={`history-chart-spinner-${global.gw.theme}-${forceUpdate}`}
                    sx={{
                        fontSize: "50px",
                        color: getThemeColor('#333333', 'rgb(244, 204, 142)'),
                        margin: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "absolute",
                        right: "0",
                        left: "0%",
                        top: "0",
                        bottom: "0",
                        background: getThemeColor('rgba(255, 255, 255, 0.8)', 'rgba(0, 0, 0, 0.5)'),
                    }}
                >
                    <MuiMaterials.RotatingLines
                        strokeColor={getThemeColor('#333333', '#f4cc8e')}
                        strokeWidth="3"
                        animationDuration="0.72"
                        width="48"
                        visible={true}
                    />
                </MuiMaterials.Box> : null}
            </MuiMaterials.Box>
        </MuiMaterials.Box>
        
    );
}
export default SensorTemperatureGraph;